import moment, { type MomentInput } from "moment/moment";
import type { TimeFrame } from "@/types";
import type { UnixTimeFrame } from "@/_helpers/api.types";
import { i18n } from "@/plugins/i18n";

/**
 * Converts a TimeFrame (start/end) to Unix timestamps using local midnight boundaries.
 * Uses `.utc()` after snapping to day boundaries so the result is always UTC-based.
 *
 * @param timeFrame - Object with `start` and `end` values accepted by moment
 * @returns `{ fromTime, toTime }` in milliseconds
 */
export const convertTimeFrameToUnix = ({ start, end }: TimeFrame): UnixTimeFrame => {
  return {
    fromTime: moment(start).startOf("day").utc().valueOf(),
    toTime: moment(end).endOf("day").utc().valueOf(),
  };
};

/**
 * Converts a TimeFrame (start/end) to Unix timestamps treating inputs as UTC.
 * Unlike `convertTimeFrameToUnix`, parses the values as UTC before snapping boundaries.
 *
 * @param timeFrame - Object with `start` and `end` values accepted by moment
 * @returns `{ fromTime, toTime }` in milliseconds
 */
export const convertTimeFrameToUTCUnix = ({ start, end }: TimeFrame): UnixTimeFrame => {
  return {
    fromTime: moment.utc(start).startOf("day").valueOf(),
    toTime: moment.utc(end).endOf("day").valueOf(),
  };
};

/**
 * Get the number of days until a given timestamp.
 * If the date is the same day, it returns the days as is.
 * If the date is in the past, it prepends a '-' sign.
 *
 * @param {number} timestamp - The target timestamp in milliseconds.
 * @returns {string} - The formatted number of days until the timestamp.
 */
export const getDaysUntil = (timestamp: number): string => {
  const now = moment();
  const targetDate = moment(timestamp);

  // Handle same day, past, and future cases
  if (now.isSame(targetDate, "day")) {
    return i18n.global.t("general.days", { n: 0 }, 0);
  }

  return targetDate.isBefore(now) ? `-${targetDate.from(now, true)}` : targetDate.from(now, true);
};

/**
 * Formats a date/time value using Moment.js with an optional format string.
 *
 * ### Format tokens (moment.js)
 * - `'LL'`   → `November 15, 2024`
 * - `'L'`    → `11/15/2024`
 * - `'LLL'`  → `November 15, 2024 4:29 PM`
 * - `'lll'`  → `Nov 15, 2024 4:29 PM`
 * - `'LLLL'` → `Friday, November 15, 2024 4:29 PM`
 * - `'llll'` → `Fri, Nov 15, 2024 4:29 PM` (default)
 *
 * ### Examples
 * ```typescript
 * getFormattedDateTime(new Date(), "LL"); // Outputs: "November 15, 2024"
 * getFormattedDateTime();                // Outputs the current date/time in "Fri, Nov 15, 2024 4:29 PM" format
 * getFormattedDateTime("2024-11-15", "L"); // Outputs: "11/15/2024"
 * ```
 *
 * @returns {string | undefined} The formatted date/time as a string, or `undefined` if no date is provided.
 */
export const getFormattedDateTime = (date?: MomentInput, format: string = "llll") => {
  if (date) {
    return moment(date).format(format);
  }
};

/**
 * Formats a given date relative to the current date/time using Moment.js.
 *
 * @param {MomentInput} [date] - The date to format. If omitted, the function returns `undefined`.
 *
 * ### Description
 * This function uses Moment.js's `.calendar()` method to format a given date in a relative, human-readable way.
 * It outputs strings such as "Today at 4:30 PM", "Yesterday at 3:00 PM", or "Last Monday at 2:15 PM" depending
 * on the proximity of the date to the current time.
 *
 * ### Examples
 * ```typescript
 * formatDistanceToNow(new Date());
 * // Outputs: "Today at 4:29 PM" (assuming the current time is 4:29 PM)
 *
 * formatDistanceToNow("2024-11-14");
 * // Outputs: "Thursday at 12:00 AM" (or a similar string based on your locale)
 *
 * formatDistanceToNow(moment().subtract(1, "days").toDate());
 * // Outputs: "Yesterday at 4:29 PM"
 *
 * formatDistanceToNow();
 * // Outputs: undefined (no date provided)
 * ```
 *
 * @returns {string | undefined} A human-readable string describing the relative time, or `undefined` if no date is provided.
 */
export const formatDistanceToNow = (date?: MomentInput) => {
  if (date) {
    return moment(date).calendar();
  }
};

/**
 * Returns days remaining in free trial as a human-readable string.
 * Uses custom thresholds: anything above 1 second shows as hours/days (never months)
 * up to 365 days, as per product requirements.
 *
 * @param trialEnds {number | null | undefined} - Timestamp when the trial ends.
 * @returns {string} e.g. "2 days", "an hour", "Invalid date" for null input.
 */
export const getDaysUntilTrialEnds = (trialEnds?: number | null) => {
  const currentTime = moment().utc();
  const trialEndsTime = moment(trialEnds).utc();
  // 'moment().duration().humanize' receives thresholds as param -
  // it means that everything more a second should be shown as an hour and until 365 days we show days, not months (180 days, not 6 months) as per Carmel's request
  // The case more than 365 days shouldn't worry us as it's for Trial and maximum trial should be only 2 months (60 days)
  return moment.duration(trialEndsTime.diff(currentTime)).humanize({ d: 365, m: 1, s: 1, ss: 1 });
};
